<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import moment from "moment-timezone";

import { toCalendarDayUtcDate } from "@/helpers/date";

import type { LastContactModalProps, SaveLastContactPayload } from "@/components/modal/Profile/lastContact/types";
import type { DropdownItemProps } from "@/dsComponents/Dropdown";
import type { SelectOption } from "@/dsComponents/inputs/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";
import { DropdownItemColors, DropdownItemIconStyle } from "@/dsComponents/Dropdown";
import Select from "@/dsComponents/inputs/Select.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import lastContactService from "@/components/modal/Profile/lastContact/services/lastContactService";
import { ContactType, LastContactMode } from "@/components/modal/Profile/lastContact/types";

const { t } = useI18n();
const toaster = useToast();

const DATE_FORMAT = "ddd D MMM, YY";

const props = defineProps<LastContactModalProps>();
const emit = defineEmits<{
    close: [];
}>();

const contactDateRaw = ref(new Date());
const contactType = ref(ContactType.CALL);
const comment = ref("");
const isLoadingSave = ref(false);

const title = computed(() => props.title ?? t("modals.companyNeed.lastContact.title.lastContact"));

const contactDate = computed(() => moment(contactDateRaw.value).locale("fr").format(DATE_FORMAT));

const minContactDate = computed(() => {
    if (props.mode !== LastContactMode.COMPANY_NEED || !props.companyNeedCreatedAt) return undefined;
    return new Date(props.companyNeedCreatedAt);
});

const contactTypeIcon = computed(() => contactTypeOptions.value.find((option) => option.value === contactType.value)?.iconLeft);

const contactTypeOptionsChecked = (type: ContactType): Partial<DropdownItemProps> | undefined => {
    if (type !== contactType.value) return undefined;

    return {
        labelColor: DropdownItemColors.black,
        contentRight: {
            icon: "check",
            iconStyle: DropdownItemIconStyle.rounded,
            iconColor: DropdownItemColors.blue,
        },
    };
};

const contactTypeOptions = computed<SelectOption[]>(() => [
    {
        label: t("modals.companyNeed.lastContact.fields.contactType.options.call"),
        value: ContactType.CALL,
        iconLeft: "phone",
        ...contactTypeOptionsChecked(ContactType.CALL),
    },
    {
        label: t("modals.companyNeed.lastContact.fields.contactType.options.voiceMessage"),
        value: ContactType.VOICE_MESSAGE,
        iconLeft: "record-mail",
        ...contactTypeOptionsChecked(ContactType.VOICE_MESSAGE),
    },
    {
        label: t("modals.companyNeed.lastContact.fields.contactType.options.sms"),
        value: ContactType.SMS,
        iconLeft: "message",
        ...contactTypeOptionsChecked(ContactType.SMS),
    },
    {
        label: t("modals.companyNeed.lastContact.fields.contactType.options.email"),
        value: ContactType.EMAIL,
        iconLeft: "mail",
        ...contactTypeOptionsChecked(ContactType.EMAIL),
    },
    {
        label: t("modals.companyNeed.lastContact.fields.contactType.options.agencyVisit"),
        value: ContactType.AGENCY_VISIT,
        iconLeft: "door-arrow-left",
        ...contactTypeOptionsChecked(ContactType.AGENCY_VISIT),
    },
]);

const handleClose = () => {
    props.onCancel?.();
    emit("close");
};

const handleDatePicked = (value: string) => {
    const utcDate = toCalendarDayUtcDate(value, DATE_FORMAT);
    if (utcDate) {
        contactDateRaw.value = utcDate;
    }
};

const handleContactTypeSelect = (option: SelectOption) => {
    contactType.value = option.value as ContactType;
};

const handleSave = async () => {
    isLoadingSave.value = true;
    try {
        const payload: SaveLastContactPayload = {
            workerIds: props.workerIds,
            contactedAt: contactDateRaw.value,
            contactType: contactType.value,
            comment: comment.value,
        };

        if (props.mode === LastContactMode.COMPANY_NEED) {
            await lastContactService.saveLastContactByCompanyNeed(props.companyNeedId, payload);
        } else {
            await lastContactService.saveLastContactByProfile(payload);
        }

        await props.onSuccess?.(payload);
        handleClose();
    } catch (error) {
        console.error(error);
        toaster?.open({
            message: t("errors.Global.ServerError._default") as string,
            type: "error",
            duration: 3000,
        });
    } finally {
        isLoadingSave.value = false;
    }
};
</script>

<template>
    <Modal
        class="profile-last-contact-modal"
        width="500px"
        :title="title"
        :open="props.open"
        close
        @close="handleClose"
    >
        <template #default>
            <div class="profile-last-contact-modal__content">
                <div class="profile-last-contact-modal__field">
                    <label class="profile-last-contact-modal__label typo-sm-medium">
                        {{ t("modals.companyNeed.lastContact.fields.contactDate.label") }}
                    </label>
                    <DatePicker
                        :value="contactDate"
                        :format="DATE_FORMAT"
                        :minDate="minContactDate"
                        :max-date="new Date()"
                        :clearable="false"
                        full-width
                        size="md"
                        class="profile-last-contact-modal__date-picker"
                        @date-picked="handleDatePicked"
                    />
                </div>
                <Select
                    v-model="contactType"
                    :trigger-icon-left="contactTypeIcon"
                    size="sm"
                    :label="t('modals.companyNeed.lastContact.fields.contactType.label')"
                    :placeholder="t('modals.companyNeed.lastContact.fields.contactType.placeholder')"
                    width="100%"
                    :options="contactTypeOptions"
                    icon="selector"
                    @select="handleContactTypeSelect"
                />
                <TextareaInput
                    v-model="comment"
                    :label="t('modals.companyNeed.lastContact.fields.comment.label')"
                    :placeholder="t('modals.companyNeed.lastContact.fields.comment.placeholder')"
                    :rows="5"
                />
            </div>
        </template>
        <template #footer>
            <div class="profile-last-contact-modal__footer">
                <ButtonAlt type="neutral" :label="t('global.actions.cancel')" @click="handleClose" />
                <ButtonAlt variant="filled" :label="t('global.actions.save')" :loading="isLoadingSave" @click="handleSave" />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.profile-last-contact-modal {
    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm, 6px);
    }

    &__label {
        color: var(--text-main-900, #00061a);
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-lg, 12px);
    }

    &__date-picker:deep(.ds-combo-button) {
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
    }
}
</style>
